/** @jsxImportSource theme-ui */
import FocusTrap from 'focus-trap-react';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Image, Menu } from 'semantic-ui-react';
import { ThemeUIStyleObject } from 'theme-ui';

import { AppStateContext } from 'api/AppStateContext';
import { InsightsState } from 'app-redux/InsightsState';
import theme from 'theme';
import { isKeyboardSelect } from 'utils/accessibilityHelpers';
import { isUserAdmin } from 'utils/authHelpers';
import { CookieKeys, RouterLinksToRememberInPrefMainPage } from 'utils/constants';
import docCookies from 'utils/cookieHelpers';
import { extractTimeframeStartDate } from 'utils/routerUtils';

import { BdIcon } from 'components/_common/BdIcon';
import { InsightsIconLiteral } from 'components/_common/BdIcon/BdIcon';

import { fetchRelevantDiseaseActivities } from 'diseaseActivitiesRelevant/diseaseActivitiesRelevant.saga';
import { setStartDate } from 'diseaseActivitiesTimeFrame/diseaseActivitiesTimeFrame.saga';
import diseaseActivitiesTimeFrameSelectors from 'diseaseActivitiesTimeFrame/diseaseActivitiesTimeFrame.selectors';
import { fetchDiseases } from 'diseases/diseases.saga';
import diseasesSelectors from 'diseases/diseases.selectors';
import { fetchLocations } from 'locations/locations.saga';
import locationsSelectors from 'locations/locations.selectors';
import * as UserSelectors from 'user/user.selectors';

import hamburgerSvg from 'assets/hamburger-menu-16x16.svg';
import logoSvg from 'assets/logo.svg';

import NavbarItem from './NavbarItem/NavbarItem';
import NavbarMenu from './NavbarItem/NavbarMenu';
import NavigationDrawer from './NavigationDrawer/NavigationDrawer';
import { AdminNavUrls, FeatureNavUrls, SettingNavUrls } from './NavLinks';

declare const $;

export interface NavigationbarUrl {
  title: string;
  routerLink?: string;
  isExternalLink?: boolean;
  onClick?: () => void;
  children?: NavigationbarUrl[];
  mobile?: boolean;
  header?: boolean;
  divider?: boolean;
  isBeta?: boolean;
  direction?: 'left' | 'right';
  icon?: InsightsIconLiteral;
}

const Styles: ThemeUIStyleObject = {
  zIndex: 101,
  '& .ui.inverted.menu': {
    border: 'none',
    background: theme.colors.deepSea90,
    mb: 0,
    fontSize: '0.75rem',
    height: ['44px', null, 'initial'],
    '& .item': {
      '&:before': { background: 'none', width: 0 },
      '& > i.icon': { ml: '6px' },
    },
    '& .nav-drawer-wrapper': {
      display: ['block', null, 'none'],
      marginLeft: 'auto',
      '& > .item.menu-toggle': {
        p: '14px 16px',
        '& > img': { width: '16px', margin: 0 }, // Overrides semantic ui
        '& > .icon.bd-icon.icon-close': { fontSize: '16px' },
        '&:hover': { bg: theme.colors.deepSea80 },
        '&:focus': { bg: theme.colors.deepSea80 },
      },
    },
  },
  '& .nav-item-container': {
    display: ['none', null, 'flex'],
    justifyContent: 'flex-end',
    flex: 1,
    '& a > .menu-item': { py: '12px' },
    '& > .dropdown.menu-item:last-child:before': {
      borderLeft: `1px solid ${theme.colors.deepSea50}`,
      width: '100%',
      top: '25%',
      height: '24px',
    },
    [`& > .dropdown.menu-item:hover, 
        > a > .menu-item:hover, 
        > .dropdown.menu-item.open, 
        > .dropdown.menu-item:focus-visible, 
        > a:focus-visible`]: {
      outline: 'none',
      position: 'relative',
      '& > .display, > .menu-item > .display': { color: theme.colors.deepSea40 },
      '&:after': {
        content: `''`,
        position: 'absolute',
        bottom: '0',
        left: '16px',
        right: '16px',
        borderBottom: `4px solid ${theme.colors.deepSea60}`,
      },
    },
  },
};

export const Navigationbar: React.FC = () => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const { amendState } = useContext(AppStateContext);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const userProfile = useSelector(UserSelectors.userProfileSelector);

  const startDate = useSelector(diseaseActivitiesTimeFrameSelectors.getStartDate);
  const locationIds: number[] = useSelector<InsightsState, number[]>(
    locationsSelectors.getAllLocationIds
  );
  const diseaseIds: number[] = useSelector<InsightsState, number[]>(
    diseasesSelectors.getAllDiseaseIds
  );

  useEffect(() => {
    dispatch(setStartDate(extractTimeframeStartDate(search)[0]));
    dispatch(fetchLocations());
    dispatch(fetchDiseases());
  }, []);

  useEffect(() => {
    dispatch(fetchRelevantDiseaseActivities({ locationIds, diseaseIds, startDate }));
  }, [locationIds, diseaseIds, startDate]);

  useEffect(() => {
    const newStartDate = extractTimeframeStartDate(search)[0];
    if (startDate !== newStartDate) {
      dispatch(setStartDate(newStartDate));
    }
  }, [search]);

  const navURLs: NavigationbarUrl[] = [...FeatureNavUrls, ...SettingNavUrls];

  const handleNavItemClick = (routerLink: string) => {
    setIsDrawerOpen(false);

    if (routerLink?.length) {
      amendState({
        isTimeframeDropdownExpanded: false,
        isCustomTimeframeDropdownExpanded: false,
        isPanelDropdownExpanded: false,
      });
      if (RouterLinksToRememberInPrefMainPage.includes(routerLink))
        docCookies.setItem(CookieKeys.PREF_MAIN_PAGE, routerLink, Infinity);
    }
  };

  return (
    <>
      <div id="nav-toolbar" sx={Styles}>
        <Menu attached="top" inverted>
          <Menu.Item>
            <Image src={logoSvg} alt="bluedot-logo" size="small" />
          </Menu.Item>
          <FocusTrap
            active={isDrawerOpen}
            // Work around for focus trap hi-jacking escape and not letting menu close
            focusTrapOptions={{
              escapeDeactivates: true,
              onDeactivate: () => {
                setIsDrawerOpen(false);
              },
            }}
          >
            <div className="nav-drawer-wrapper">
              <Menu.Item
                tabIndex={0}
                className="menu-toggle"
                aria-label={isDrawerOpen ? 'close navigation menu' : 'open navigation menu'}
                onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                onKeyPress={e => isKeyboardSelect(e) && setIsDrawerOpen(!isDrawerOpen)}
              >
                {isDrawerOpen ? (
                  <BdIcon className="toggle-img" name="icon-close" nomargin />
                ) : (
                  <img
                    className="toggle-img"
                    src={hamburgerSvg}
                    height="16px"
                    width="16px"
                    alt="≡"
                  />
                )}
              </Menu.Item>
              <NavigationDrawer
                isOpen={isDrawerOpen}
                adminUser={isUserAdmin(userProfile)}
                handleCancel={() => setIsDrawerOpen(false)}
                onItemClick={handleNavItemClick}
              />
            </div>
          </FocusTrap>
          <div className="nav-item-container">
            {navURLs.map(urlOpts => (
              <React.Fragment key={urlOpts.title}>
                {urlOpts.children?.length ? (
                  <NavbarMenu options={urlOpts} onClick={handleNavItemClick} />
                ) : (
                  <NavbarItem options={urlOpts} onClick={handleNavItemClick} />
                )}
              </React.Fragment>
            ))}
          </div>
        </Menu>
      </div>
    </>
  );
};

export default Navigationbar;
